import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BulkProgressService } from './services/bulk-progress.service';
import { BulkProgressExcelExporter } from './utils/excel-export.util';
import {
  BulkProgressResponse,
  ChildApplicationDto,
  ProgressLabels,
  ProgressState,
  CompletionEvent,
  FailureEvent,
  BillReadyEvent,
  ProgressError,
  DEFAULT_PROGRESS_LABELS,
} from './models/bulk-progress.models';

@Component({
  selector: 'irembogov-bulk-application-progress-tracker',
  templateUrl: './irembogov-bulk-application-progress-tracker.component.html',
  styleUrls: ['./irembogov-bulk-application-progress-tracker.component.scss'],
  providers: [BulkProgressService],
})
export class IrembogovBulkApplicationProgressTrackerComponent
  implements OnInit, OnDestroy
{
  // ============================================================================
  // INPUTS
  // ============================================================================

  @Input() applicationNumber!: string;
  @Input() pollInterval = 3000;
  @Input() labels: ProgressLabels = {};
  @Input() autoStart = true;

  // ============================================================================
  // OUTPUTS
  // ============================================================================

  @Output() progressUpdate = new EventEmitter<BulkProgressResponse>();
  @Output() completed = new EventEmitter<CompletionEvent>();
  @Output() allFailed = new EventEmitter<FailureEvent>();
  @Output() billReady = new EventEmitter<BillReadyEvent>();
  @Output() errorOccurred = new EventEmitter<ProgressError>();
  @Output() reportDownloaded = new EventEmitter<void>();

  // ============================================================================
  // PUBLIC PROPERTIES
  // ============================================================================

  currentProgress: BulkProgressResponse | null = null;
  failedApplications: ChildApplicationDto[] = [];
  isLoading = false;
  isLoadingFailedApps = false;
  hasError = false;
  errorMessage = '';

  // Computed properties for template
  get mergedLabels(): Required<ProgressLabels> {
    return { ...DEFAULT_PROGRESS_LABELS, ...this.labels };
  }

  get isInProgress(): boolean {
    return this.currentProgress?.status === ProgressState.IN_PROGRESS;
  }

  get isCompleted(): boolean {
    return this.currentProgress?.status === ProgressState.COMPLETED;
  }

  get isPartialFailure(): boolean {
    return this.currentProgress?.status === ProgressState.PARTIAL_FAILURE;
  }

  get isCompleteFailure(): boolean {
    return this.currentProgress?.status === ProgressState.COMPLETE_FAILURE;
  }

  get hasFailedApplications(): boolean {
    return (this.currentProgress?.totalFailedChildApplications ?? 0) > 0;
  }

  get isWaitingForProcessing(): boolean {
    // Show loading when totalExpectedChildApplications is 0
    if (!this.currentProgress) return false;
    return this.currentProgress.totalExpectedChildApplications === 0;
  }

  get isWaitingForBillId(): boolean {
    if (!this.currentProgress) return false;

    const totalProcessed =
      this.currentProgress.totalSuccessfulChildApplications +
      this.currentProgress.totalFailedChildApplications;
    const allProcessed =
      totalProcessed === this.currentProgress.totalExpectedChildApplications;
    const noBillId =
      !this.currentProgress.billId || this.currentProgress.billId === '';

    return allProcessed && noBillId && totalProcessed > 0;
  }

  get isProcessingComplete(): boolean {
    if (!this.currentProgress) return false;
    const totalProcessed =
      this.currentProgress.totalSuccessfulChildApplications +
      this.currentProgress.totalFailedChildApplications;
    return (
      totalProcessed === this.currentProgress.totalExpectedChildApplications
    );
  }

  // Expose ProgressState enum to template
  ProgressState = ProgressState;

  // ============================================================================
  // PRIVATE PROPERTIES
  // ============================================================================

  private destroy$ = new Subject<void>();

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  constructor(
    private bulkProgressService: BulkProgressService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.validateInputs();
    if (this.autoStart) {
      this.startPolling();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Manually start polling (if autoStart is false)
   */
  startPolling(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    this.bulkProgressService
      .pollProgress(this.applicationNumber, this.pollInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: progress => this.handleProgressUpdate(progress),
        error: err => this.handleError(err),
        complete: () => this.handlePollingComplete(),
      });
  }

  /**
   * Download failed applications report
   */
  downloadFailedReport(): void {
    if (this.failedApplications.length === 0) {
      this.loadFailedApplications(() => this.exportFailedApplications());
    } else {
      this.exportFailedApplications();
    }
  }

  /**
   * Load failed applications on demand
   */
  loadFailedApplications(callback?: () => void): void {
    if (this.isLoadingFailedApps || !this.hasFailedApplications) return;

    this.isLoadingFailedApps = true;

    this.bulkProgressService
      .fetchFailedApplications(this.applicationNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.failedApplications = response.applications;
          this.isLoadingFailedApps = false;
          if (callback) callback();
        },
        error: err => {
          this.isLoadingFailedApps = false;
          this.handleError(err);
        },
      });
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private validateInputs(): void {
    if (!this.applicationNumber) {
      throw new Error('[BulkProgressTracker] applicationNumber is required');
    }
  }

  private handleProgressUpdate(progress: BulkProgressResponse): void {
    this.currentProgress = progress;
    this.progressUpdate.emit(progress);

    // Only load failed apps when processing is complete
    if (
      this.isProcessingComplete &&
      progress.totalFailedChildApplications > 0 &&
      this.failedApplications.length === 0 &&
      !this.isLoadingFailedApps
    ) {
      this.loadFailedApplications();
    }

    // Emit specific events based on status
    if (progress.status === ProgressState.COMPLETED && progress.billId) {
      this.billReady.emit({
        applicationNumber: progress.applicationNumber,
        billId: progress.billId,
      });
    }

    if (progress.status === ProgressState.COMPLETE_FAILURE) {
      this.allFailed.emit({
        applicationNumber: progress.applicationNumber,
        totalFailed: progress.totalFailedChildApplications,
      });
    }

    if (
      progress.status === ProgressState.COMPLETED ||
      progress.status === ProgressState.PARTIAL_FAILURE
    ) {
      this.completed.emit({
        applicationNumber: progress.applicationNumber,
        billId: progress.billId || '',
        totalSuccessful: progress.totalSuccessfulChildApplications,
        totalFailed: progress.totalFailedChildApplications,
      });
    }
  }

  private handlePollingComplete(): void {
    this.isLoading = false;
  }

  private handleError(err: any): void {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage = err.message || 'An error occurred while processing';

    this.errorOccurred.emit({
      message: this.errorMessage,
      error: err,
    });
  }

  private exportFailedApplications(): void {
    // Translate error messages before export
    const translatedApplications = this.failedApplications.map(app => ({
      ...app,
      errorMessage: this.translateErrorMessage(app),
    }));

    // Translate headers
    const translatedIdentifierHeader = this.translateService.instant(
      this.mergedLabels.identifierHeader
    );
    const translatedErrorMessageHeader = this.translateService.instant(
      this.mergedLabels.errorMessageHeader
    );
    const translatedNoErrorMessage = this.translateService.instant(
      'BULK_PROGRESS.NO_ERROR_MESSAGE'
    );

    BulkProgressExcelExporter.exportFailedApplications(
      translatedApplications,
      this.applicationNumber,
      {
        identifier: translatedIdentifierHeader,
        errorMessage: translatedErrorMessageHeader,
        noErrorMessage: translatedNoErrorMessage,
      }
    );
    this.reportDownloaded.emit();
  }

  /**
   * Translate error message for a failed application.
   * Supports interpolation using errorMetadata for dynamic values.
   * Tries errorCode first, then errorMessage as translation keys.
   */
  private translateErrorMessage(app: ChildApplicationDto): string {
    const interpolateParams = app.errorMetadata || {};

    // Try errorCode first
    if (app.errorCode) {
      const translated = this.translateService.instant(
        app.errorCode,
        interpolateParams
      );
      if (translated !== app.errorCode) {
        return translated;
      }
    }

    // Try errorMessage as a translation key
    if (app.errorMessage) {
      const translated = this.translateService.instant(
        app.errorMessage,
        interpolateParams
      );
      if (translated !== app.errorMessage) {
        return translated;
      }
    }

    return app.errorMessage || '';
  }
}
